import Phaser from "phaser";
import { extractStrictJsonObject, stripStrategyPayload } from "../../../shared/commanderText";
import type { CommanderConnectionStatus } from "../ai/commanderSocket";
import type { Language } from "../i18n";
import { localizeCommanderLine, moodLabel, t } from "../i18n";

type ChatLine = {
  speaker: "commander" | "player" | "system";
  text: string;
  mood?: string;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export class CommanderChat {
  private readonly root: HTMLDivElement;
  private readonly toggleEl: HTMLButtonElement;
  private readonly nameEl: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly statusEl: HTMLDivElement;
  private readonly moodEl: HTMLDivElement;
  private readonly logEl: HTMLDivElement;
  private readonly inputEl: HTMLInputElement;
  private readonly resizeHandler = () => this.updatePosition();
  private readonly lines: ChatLine[] = [];
  private readonly recentCommanderLines = new Map<string, number>();
  private commanderName = "";
  private latestLine = "";
  private sendHandler?: (text: string) => void;

  constructor(private readonly scene: Phaser.Scene, private readonly language: Language, private readonly uiScale = 1, highContrastText = false) {
    this.root = document.createElement("div");
    this.root.className = `commander-chat-panel is-collapsed${highContrastText ? " high-contrast" : ""}`;
    this.root.innerHTML = `
      <button class="commander-chat-toggle" type="button" aria-label="Toggle commander chat">+</button>
      <div class="commander-chat-head">
        <div class="commander-chat-portrait" aria-hidden="true"></div>
        <div class="commander-chat-meta">
          <div class="commander-chat-name"></div>
          <div class="commander-chat-title"></div>
          <div class="commander-chat-status"></div>
          <div class="commander-chat-mood"></div>
          <div class="commander-chat-latest"></div>
        </div>
      </div>
      <div class="commander-chat-log" role="log" aria-live="polite"></div>
      <div class="commander-chat-compose">
        <input class="commander-chat-input" type="text" autocomplete="off" />
      </div>
    `;

    this.toggleEl = this.root.querySelector(".commander-chat-toggle") as HTMLButtonElement;
    this.nameEl = this.root.querySelector(".commander-chat-name") as HTMLDivElement;
    this.titleEl = this.root.querySelector(".commander-chat-title") as HTMLDivElement;
    this.statusEl = this.root.querySelector(".commander-chat-status") as HTMLDivElement;
    this.moodEl = this.root.querySelector(".commander-chat-mood") as HTMLDivElement;
    this.logEl = this.root.querySelector(".commander-chat-log") as HTMLDivElement;
    this.inputEl = this.root.querySelector(".commander-chat-input") as HTMLInputElement;
    this.inputEl.placeholder = t(this.language, "chatPlaceholder");

    const host = document.getElementById("game") ?? document.body;
    host.appendChild(this.root);

    this.root.addEventListener("pointerdown", (event) => event.stopPropagation());
    // NOTE: no "wheel" stopPropagation here — it used to swallow wheel-zoom when the
    // cursor was over the chat panel. Page scrolling is already blocked globally by
    // overscroll-behavior:none + touch-action:none in styles.css.
    this.toggleEl.addEventListener("click", () => this.toggleCollapsed());
    this.inputEl.addEventListener("keydown", (event) => this.handleInputKey(event));
    window.addEventListener("resize", this.resizeHandler);
    this.scene.scale.on("resize", this.resizeHandler);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    this.scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());

    this.setCommander(t(this.language, "aiCommander"), t(this.language, "rivalCommander"));
    this.setConnectionStatus("connecting");
    this.latestLine = t(this.language, "chatPrompt");
    this.renderLines();
    this.updatePosition();
  }

  setCommander(name: string, title: string) {
    this.commanderName = this.language === "ko" ? this.localCommanderName(name) : name;
    this.nameEl.textContent = this.commanderName;
    this.titleEl.textContent = this.language === "ko" ? this.localCommanderTitle(title) : title;
  }

  setTheme(visualTheme: string | undefined) {
    const theme = commanderTheme(visualTheme ?? "");
    this.root.style.setProperty("--commander-accent", theme.accent);
    this.root.style.setProperty("--commander-accent-soft", theme.soft);
    this.root.style.setProperty("--commander-panel-top", theme.panelTop);
    this.root.style.setProperty("--commander-panel-bottom", theme.panelBottom);
  }

  setSendHandler(handler: (text: string) => void) {
    this.sendHandler = handler;
  }

  setConnectionStatus(status: CommanderConnectionStatus) {
    this.root.dataset.connection = status;
    this.statusEl.textContent = this.connectionLabel(status);
  }

  pushTaunt(taunt: string, mood: string) {
    const line = this.cleanLine(localizeCommanderLine(this.language, taunt));
    if (!line) return;
    const now = this.scene.time.now;
    const lastSeen = this.recentCommanderLines.get(line) ?? -Infinity;
    if (now - lastSeen > 45000 && !this.lines.slice(-4).some((item) => item.speaker === "commander" && item.text === line)) {
      this.lines.push({ speaker: "commander", text: line, mood });
      this.latestLine = line;
      this.recentCommanderLines.set(line, now);
      this.renderLines(true);
    }
    this.moodEl.textContent = moodLabel(this.language, mood);
    this.root.dataset.mood = mood;
  }

  private handleInputKey(event: KeyboardEvent) {
    if (event.key !== "Enter" || event.isComposing) return;
    const text = this.inputEl.value.trim();
    if (!text) return;
    this.inputEl.value = "";
    this.lines.push({ speaker: "player", text });
    this.latestLine = `${t(this.language, "you")}: ${text}`;
    this.renderLines(true);
    this.sendHandler?.(text);
    event.preventDefault();
  }

  private renderLines(forceBottom = false) {
    const atBottom = this.logEl.scrollTop + this.logEl.clientHeight >= this.logEl.scrollHeight - 12;
    this.logEl.replaceChildren(
      ...this.lines.slice(-40).map((line) => {
        const item = document.createElement("div");
        item.className = `commander-chat-line ${line.speaker}`;
        const label = document.createElement("span");
        label.className = "commander-chat-speaker";
        label.textContent = line.speaker === "player" ? t(this.language, "you") : line.speaker === "system" ? "" : this.shortCommanderName();
        const body = document.createElement("span");
        body.className = "commander-chat-body";
        body.textContent = line.text;
        item.append(label, body);
        return item;
      })
    );
    if (forceBottom || atBottom) {
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }
    const latest = this.root.querySelector(".commander-chat-latest") as HTMLDivElement | null;
    if (latest) latest.textContent = this.latestLine;
  }

  private cleanLine(text: string): string {
    const raw = text.trim();
    const json = extractStrictJsonObject(raw);
    if (json && typeof json === "object") {
      const record = json as Record<string, unknown>;
      const candidate = record.taunt ?? record.reply ?? record.message ?? record.commanderComment;
      if (typeof candidate === "string") return this.cleanLine(candidate);
      return "";
    }
    const trimmed = stripStrategyPayload(raw).trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      return "";
    }
    return trimmed.length > 180 ? `${trimmed.slice(0, 177)}...` : trimmed;
  }

  private toggleCollapsed() {
    const collapsed = !this.root.classList.contains("is-collapsed");
    this.root.classList.toggle("is-collapsed", collapsed);
    this.toggleEl.textContent = collapsed ? "+" : "-";
    this.updatePosition();
    if (!collapsed) {
      requestAnimationFrame(() => {
        this.logEl.scrollTop = this.logEl.scrollHeight;
      });
    }
  }

  private shortCommanderName() {
    return this.commanderName || t(this.language, "aiCommander");
  }

  private connectionLabel(status: CommanderConnectionStatus) {
    switch (status) {
      case "connecting":
        return t(this.language, "commanderConnecting");
      case "connected":
        return t(this.language, "commanderConnected");
      case "healthy":
        return t(this.language, "commanderHealthy");
      case "recovering":
        return t(this.language, "commanderRecovering");
      case "misconfigured":
        return t(this.language, "commanderMisconfigured");
      case "health-failed":
        return t(this.language, "commanderHealthFailed");
      case "error":
        return t(this.language, "commanderError");
      case "offline":
      default:
        return t(this.language, "commanderOffline");
    }
  }

  private updatePosition() {
    const canvas = this.scene.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / 1280;
    const scaleY = rect.height / 720;
    const collapsed = this.root.classList.contains("is-collapsed");
    const margin = 6;
    const width = Math.min((collapsed ? 278 : 304) * scaleX, window.innerWidth - margin * 2);
    const height = Math.min((collapsed ? 66 : 196) * scaleY, window.innerHeight - margin * 2);
    const left = clamp(rect.left + 982 * scaleX, margin, window.innerWidth - width - margin);
    const top = clamp(rect.top + 12 * scaleY, margin, window.innerHeight - height - margin);
    this.root.style.left = `${left}px`;
    this.root.style.top = `${top}px`;
    this.root.style.width = `${width}px`;
    this.root.style.height = `${height}px`;
    this.root.style.setProperty("--chat-scale", `${Math.max(0.62, Math.min(scaleX, scaleY)) * this.uiScale}`);
  }

  private destroy() {
    window.removeEventListener("resize", this.resizeHandler);
    this.scene.scale.off("resize", this.resizeHandler);
    this.root.remove();
  }

  private localCommanderName(name: string) {
    if (name.includes("Mordren")) return "모드렌 블랙월";
    if (name.includes("Varkos")) return "바르코스 엠버베인";
    if (name.includes("Cyrak")) return "시락 잿빛군주";
    if (name.includes("Velka")) return "벨카 쏜마셜";
    return name;
  }

  private localCommanderTitle(title: string) {
    if (title.includes("절의 늪")) return "철의 늪 공성두뇌";
    if (title.includes("Broken Gate")) return "부서진 관문의 감시자";
    if (title.includes("Red Keep")) return "붉은 성채의 원수";
    if (title.includes("Iron Mire")) return "철의 늪 공성두뇌";
    if (title.includes("Oath-Breaker")) return "맹약을 깬 장군";
    return title;
  }
}

function commanderTheme(visualTheme: string) {
  const text = visualTheme.toLowerCase();
  if (text.includes("frost") || text.includes("cold") || text.includes("ice")) {
    return { accent: "#9be8ff", soft: "rgba(155, 232, 255, 0.44)", panelTop: "rgba(10, 25, 32, 0.46)", panelBottom: "rgba(7, 16, 26, 0.42)" };
  }
  if (text.includes("plague") || text.includes("mire") || text.includes("swamp") || text.includes("thorn")) {
    return { accent: "#a7e66f", soft: "rgba(167, 230, 111, 0.4)", panelTop: "rgba(16, 27, 14, 0.46)", panelBottom: "rgba(8, 18, 15, 0.42)" };
  }
  if (text.includes("shadow") || text.includes("ash") || text.includes("black iron")) {
    return { accent: "#c9b878", soft: "rgba(201, 184, 120, 0.42)", panelTop: "rgba(18, 17, 24, 0.48)", panelBottom: "rgba(8, 9, 14, 0.44)" };
  }
  return { accent: "#ff604f", soft: "rgba(255, 96, 79, 0.42)", panelTop: "rgba(28, 12, 15, 0.46)", panelBottom: "rgba(12, 14, 18, 0.42)" };
}
